import React, { useRef } from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { 
  Box, 
  IconButton, 
  LinearProgress, 
  Typography, 
  Paper,
  Chip,
  Fade,
  Alert
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { alpha } from '@mui/material/styles';

import axios from '@/lib/v2/axios';

export interface UploadedFile {
  key: string;
  path: string;
  size: number;
  name: string;
  type: string;
}

export interface FileUpload {
  id: string;
  file: File;
  preview: string;
  progress: number;
  status: 'uploading' | 'success' | 'error';
  uploadedFile?: UploadedFile;
}

export interface FileTypeConfig {
  label: string;
  extensions: string[];
  mimeTypes: string[];
  icon: string;
  maxSize?: number; // in bytes
}

export const FILE_TYPES = {
  IMAGES: {
    label: 'Images',
    extensions: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'],
    mimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'],
    icon: '🖼️',
    maxSize: 10 * 1024 * 1024, // 10MB
  },
  DOCUMENTS: {
    label: 'Documents',
    extensions: ['pdf', 'doc', 'docx'],
    mimeTypes: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    icon: '📄',
    maxSize: 25 * 1024 * 1024, // 25MB
  },
  SPREADSHEETS: {
    label: 'Spreadsheets',
    extensions: ['xls', 'xlsx', 'csv'],
    mimeTypes: ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv'],
    icon: '📊',
    maxSize: 25 * 1024 * 1024, // 25MB
  },
  PRESENTATIONS: {
    label: 'Presentations',
    extensions: ['ppt', 'pptx'],
    mimeTypes: ['application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation'],
    icon: '📈',
    maxSize: 25 * 1024 * 1024, // 25MB
  },
  ARCHIVES: {
    label: 'Archives',
    extensions: ['zip', 'rar', '7z', 'tar', 'gz'],
    mimeTypes: ['application/zip', 'application/x-rar-compressed', 'application/x-7z-compressed', 'application/x-tar', 'application/gzip'],
    icon: '📦',
    maxSize: 100 * 1024 * 1024, // 100MB
  },
  ALL: {
    label: 'All Files',
    extensions: [],
    mimeTypes: [],
    icon: '📎',
    maxSize: 100 * 1024 * 1024, // 100MB
  },
} as const;

interface FileUploaderProps {
  /**
   * Callback that returns the list of file uploads (including their updated statuses)
   */
  onUploadComplete: (fileUploads: FileUpload[]) => void;
  /**
   * Optional title for the upload area
   */
  title?: string;
  /**
   * Optional subtitle for the upload area
   */
  subtitle?: string;
  /**
   * Maximum number of files allowed
   */
  maxFiles?: number;
  /**
   * Maximum file size in bytes
   */
  maxFileSize?: number;
  /**
   * Allowed file types - can be a single type or array of types
   */
  allowedTypes?: keyof typeof FILE_TYPES | (keyof typeof FILE_TYPES)[];
  /**
   * Custom file type configuration
   */
  customFileTypes?: FileTypeConfig[];
  /**
   * Whether to show file previews (images only)
   */
  showPreviews?: boolean;
  /**
   * Custom upload endpoint
   */
  uploadEndpoint?: string;
  /**
   * Additional headers for upload
   */
  uploadHeaders?: Record<string, string>;
}

const FileUploader: React.FC<FileUploaderProps> = ({ 
  onUploadComplete, 
  title = "Upload Files",
  subtitle = "Drag & drop files here, or click to browse",
  maxFiles = 10,
  maxFileSize,
  allowedTypes = ['ALL'],
  customFileTypes = [],
  showPreviews = true,
  uploadEndpoint = '/api/v2/files/upload',
  uploadHeaders = {}
}) => {
  const [fileUploads, setFileUploads] = React.useState<FileUpload[]>([]);
  const fileUploadsRef = useRef(fileUploads);

  // Update the ref whenever fileUploads changes
  React.useEffect(() => {
    fileUploadsRef.current = fileUploads;
  }, [fileUploads]);

  // Build accepted file types from allowedTypes.
  //
  // react-dropzone expects `accept` shaped as { [mimeType]: ['.ext', ...] } —
  // one MIME type per key, with each extension dot-prefixed. Passing a
  // comma-joined string of MIME types as a key, or extensions without a
  // leading dot, makes react-dropzone log "invalid file extension" on every
  // render (which previously crashed the page).
  const toDotExtension = (ext: string): string => (ext.startsWith('.') ? ext : `.${ext}`);

  const getAcceptedFileTypes = (): Record<string, string[]> => {
    const types = Array.isArray(allowedTypes) ? allowedTypes : [allowedTypes];
    const acceptedTypes: Record<string, string[]> = {};

    types.forEach((type) => {
      if (type === 'ALL') {
        // Accept all files
        return;
      }

      const fileType = FILE_TYPES[type];
      if (fileType) {
        const dotExtensions = fileType.extensions.map(toDotExtension);
        fileType.mimeTypes.forEach((mimeType) => {
          acceptedTypes[mimeType] = dotExtensions;
        });
      }
    });

    // Add custom file types
    customFileTypes.forEach((customType) => {
      const dotExtensions = customType.extensions.map(toDotExtension);
      customType.mimeTypes.forEach((mimeType) => {
        acceptedTypes[mimeType] = dotExtensions;
      });
    });

    return acceptedTypes;
  };

  // Get all allowed extensions for validation
  const getAllowedExtensions = () => {
    const types = Array.isArray(allowedTypes) ? allowedTypes : [allowedTypes];
    const extensions: string[] = [];
    
    types.forEach(type => {
      if (type === 'ALL') return;
      
      const fileType = FILE_TYPES[type];
      if (fileType) {
        extensions.push(...fileType.extensions);
      }
    });

    customFileTypes.forEach(customType => {
      extensions.push(...customType.extensions);
    });

    return extensions;
  };

  // Get max file size from allowed types or use provided maxFileSize
  const getMaxFileSize = () => {
    if (maxFileSize) return maxFileSize;
    
    const types = Array.isArray(allowedTypes) ? allowedTypes : [allowedTypes];
    let maxSize = 0;
    
    types.forEach(type => {
      if (type === 'ALL') {
        maxSize = Math.max(maxSize, FILE_TYPES.ALL.maxSize || 0);
        return;
      }
      
      const fileType = FILE_TYPES[type];
      if (fileType?.maxSize) {
        maxSize = Math.max(maxSize, fileType.maxSize);
      }
    });

    customFileTypes.forEach(customType => {
      if (customType.maxSize) {
        maxSize = Math.max(maxSize, customType.maxSize);
      }
    });

    return maxSize || 100 * 1024 * 1024; // Default 100MB
  };

  // Validate file
  const validateFile = (file: File): string | null => {
    const allowedExtensions = getAllowedExtensions();
    const maxSize = getMaxFileSize();
    
    // Check file size
    if (maxSize && file.size > maxSize) {
      return `File size exceeds maximum allowed size of ${formatFileSize(maxSize)}`;
    }
    
    // Check file extension if not accepting all files
    if (allowedTypes.length > 0 && !allowedTypes.includes('ALL')) {
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      if (fileExtension && !allowedExtensions.includes(fileExtension)) {
        return `File type not allowed. Allowed types: ${allowedExtensions.join(', ')}`;
      }
    }
    
    return null;
  };

  // This function simulates the file upload and returns the UploadedFile info.
  const simulateUpload = (file: File, onProgress: (progress: number) => void): Promise<UploadedFile> => {
    const formData = new FormData();
    formData.append('file', file);

    return axios
      .post(uploadEndpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...uploadHeaders,
        },
        onUploadProgress: (progressEvent: any) => {
          if (progressEvent.loaded && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            onProgress(progress);
          }
        },
      })
      .then((response) => ({
        key: response.data.file_key,
        path: response.data.file_key,
        size: response.data.file_size,
        name: response.data.original_name,
        type: response.data.content_type,
      }));
  };

  // Dropzone onDrop callback
  const onDrop = React.useCallback(
    (acceptedFiles: File[], rejectedFiles: any[]) => {
      // Handle rejected files
      rejectedFiles.forEach(({ file, errors }) => {
        console.warn('File rejected:', file.name, errors);
      });

      // Check if adding these files would exceed the limit
      if (fileUploadsRef.current.length + acceptedFiles.length > maxFiles) {
        console.warn(`Cannot upload more than ${maxFiles} files`);
        return;
      }

      const uploadPromises = acceptedFiles.map((file) => {
        // Validate file
        const validationError = validateFile(file);
        if (validationError) {
          console.warn('File validation failed:', validationError);
          return Promise.resolve(null);
        }

        const alreadyAdded = fileUploadsRef.current.some(
          (upload) => upload.file.name === file.name && upload.file.size === file.size
        );
        if (alreadyAdded) return Promise.resolve(null);

        const newUpload: FileUpload = {
          id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
          file,
          preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : '',
          progress: 0,
          status: 'uploading',
        };
        setFileUploads((prev) => [...prev, newUpload]);

        return simulateUpload(file, (progress) => {
          setFileUploads((prev) =>
            prev.map((upload) => (upload.id === newUpload.id ? { ...upload, progress } : upload))
          );
        })
          .then((uploadedFile) => {
            console.log('File uploaded successfully:', uploadedFile);
            setFileUploads((prev) =>
              prev.map((upload) =>
                upload.id === newUpload.id ? { ...upload, progress: 100, status: 'success', uploadedFile } : upload
              )
            );
            return uploadedFile;
          })
          .catch((error) => {
            console.error('Error uploading file:', error);
            setFileUploads((prev) =>
              prev.map((upload) => (upload.id === newUpload.id ? { ...upload, status: 'error' } : upload))
            );
            throw error;
          });
      });

      Promise.all(uploadPromises)
        .then((results) => {
          // Just update the state, the useEffect will handle calling onUploadComplete
          setFileUploads((currentUploads) => currentUploads);
        })
        .catch((err) => {
          console.error('File upload error:', err);
        });
    },
    [maxFiles, allowedTypes, customFileTypes, maxFileSize, uploadEndpoint, uploadHeaders]
  );

  // Memoize so the `accept`/`maxSize` object identities stay stable across
  // renders — otherwise react-dropzone re-initializes on every render.
  const acceptedFileTypes = React.useMemo(
    () => getAcceptedFileTypes(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(allowedTypes), JSON.stringify(customFileTypes)]
  );
  const computedMaxSize = React.useMemo(
    () => getMaxFileSize(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(allowedTypes), JSON.stringify(customFileTypes), maxFileSize]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    disabled: fileUploads.length >= maxFiles,
    accept: acceptedFileTypes,
    maxSize: computedMaxSize,
  });

  // Cleanup for object URLs
  React.useEffect(() => {
    return () => {
      fileUploads.forEach((upload) => { 
        if (upload.preview) URL.revokeObjectURL(upload.preview); 
      });
    };
  }, [fileUploads]);

  // Notify the parent only when the set of completed uploads actually changes.
  //
  // Consumers often pass an `onUploadComplete` whose identity changes on every
  // render (e.g. a useCallback that depends on a Formik instance). Without this
  // guard, the effect would re-fire every render and call back into the parent,
  // creating an infinite update loop ("Maximum update depth exceeded").
  const reportedUploadsRef = useRef<string>('');
  React.useEffect(() => {
    const anyUploading = fileUploads.some((u) => u.status === 'uploading');
    if (anyUploading) return;

    const successfulUploads = fileUploads.filter((upload) => upload.status === 'success');
    const signature = successfulUploads.map((u) => u.id).join('|');
    if (signature === reportedUploadsRef.current) return;

    reportedUploadsRef.current = signature;
    onUploadComplete(successfulUploads);
  }, [fileUploads, onUploadComplete]);

  const handleRemoveFile = (id: string) => {
    setFileUploads((prev) => prev.filter((upload) => upload.id !== id));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))  } ${  sizes[i]}`;
  };

  const getFileIcon = (fileType: string, fileName: string) => {
    // Check custom file types first
    customFileTypes.forEach(customType => {
      if (customType.mimeTypes.includes(fileType) || 
          customType.extensions.some(ext => fileName.toLowerCase().endsWith(`.${ext}`))) {
        return customType.icon;
      }
    });

    // Check predefined file types
    if (fileType.startsWith('image/')) return FILE_TYPES.IMAGES.icon;
    if (fileType.includes('pdf') || fileType.includes('document')) return FILE_TYPES.DOCUMENTS.icon;
    if (fileType.includes('excel') || fileType.includes('spreadsheet') || fileType.includes('csv')) return FILE_TYPES.SPREADSHEETS.icon;
    if (fileType.includes('powerpoint') || fileType.includes('presentation')) return FILE_TYPES.PRESENTATIONS.icon;
    if (fileType.includes('zip') || fileType.includes('rar') || fileType.includes('archive')) return FILE_TYPES.ARCHIVES.icon;
    
    return FILE_TYPES.ALL.icon;
  };

  const getAcceptedTypesDescription = () => {
    const types = Array.isArray(allowedTypes) ? allowedTypes : [allowedTypes];
    const descriptions: string[] = [];
    
    types.forEach(type => {
      if (type === 'ALL') {
        descriptions.push('All files');
        return;
      }
      
      const fileType = FILE_TYPES[type];
      if (fileType) {
        descriptions.push(fileType.label);
      }
    });

    customFileTypes.forEach(customType => {
      descriptions.push(customType.label);
    });

    return descriptions.join(', ');
  };

  return (
    <Box>
      {/* Upload Area */}
      <Paper
        {...getRootProps()}
        elevation={isDragActive ? 8 : 1}
        sx={{
          border: `2px dashed ${isDragActive ? 'primary.main' : 'divider'}`,
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          cursor: fileUploads.length >= maxFiles ? 'not-allowed' : 'pointer',
          backgroundColor: isDragActive ? alpha('#1976d2', 0.04) : 'background.paper',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            borderColor: fileUploads.length >= maxFiles ? 'divider' : 'primary.main',
            backgroundColor: fileUploads.length >= maxFiles ? 'background.paper' : alpha('#1976d2', 0.02),
          },
          opacity: fileUploads.length >= maxFiles ? 0.6 : 1,
        }}
      >
        <input {...getInputProps()} />
        <CloudUploadIcon 
          sx={{ 
            fontSize: 48, 
            color: isDragActive ? 'primary.main' : 'text.secondary',
            mb: 2 
          }} 
        />
        <Typography color={isDragActive ? 'primary.main' : 'text.primary'} gutterBottom variant="h6">
          {isDragActive ? 'Drop files here...' : title}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 1 }} variant="body2">
          {subtitle}
        </Typography>
        <Typography color="text.secondary" sx={{ display: 'block', mb: 1 }} variant="caption">
          Accepted: {getAcceptedTypesDescription()} • Max size: {formatFileSize(getMaxFileSize())}
        </Typography>
        <Chip 
          color={fileUploads.length >= maxFiles ? 'error' : 'default'} 
          label={`${fileUploads.length}/${maxFiles} files`} 
          size="small"
          variant="outlined"
        />
      </Paper>

      {/* File List */}
      {fileUploads.length > 0 && (
        <Fade in>
          <Box sx={{ mt: 3 }}>
            <Typography gutterBottom variant="subtitle2">
              Uploaded Files ({fileUploads.length})
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {fileUploads.map((upload) => (
                <Paper
                  elevation={2}
                  key={upload.id}
                  sx={{
                    position: 'relative',
                    width: 200,
                    overflow: 'hidden',
                    borderRadius: 2,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      elevation: 4,
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  {/* Remove Button */}
                  <IconButton
                    onClick={() => { handleRemoveFile(upload.id); }}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      zIndex: 2,
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      backdropFilter: 'blur(4px)',
                      '&:hover': {
                        backgroundColor: 'error.main',
                        color: 'white',
                      },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>

                  {/* File Preview */}
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    {showPreviews && upload.file.type.startsWith('image/') && upload.preview ? (
                      <Box
                        alt={upload.file.name}
                        component="img"
                        src={upload.preview}
                        sx={{
                          width: '100%',
                          height: 120,
                          objectFit: 'cover',
                          borderRadius: 1,
                          mb: 1,
                        }}
                      />
                    ) : (
                      <Box sx={{ mb: 1 }}>
                        <Typography sx={{ mb: 1 }} variant="h4">
                          {getFileIcon(upload.file.type, upload.file.name)}
                        </Typography>
                      </Box>
                    )}

                    {/* File Info */}
                    <Typography 
                      sx={{ 
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        mb: 0.5
                      }} 
                      variant="body2"
                    >
                      {upload.file.name}
                    </Typography>
                    <Typography color="text.secondary" variant="caption">
                      {formatFileSize(upload.file.size)}
                    </Typography>

                    {/* Status */}
                    <Box sx={{ mt: 1 }}>
                      {upload.status === 'uploading' && (
                        <Box>
                          <LinearProgress 
                            sx={{ mb: 0.5 }} 
                            value={upload.progress} 
                            variant="determinate"
                          />
                          <Typography color="text.secondary" variant="caption">
                            {upload.progress}%
                          </Typography>
                        </Box>
                      )}
                      {upload.status === 'success' && (
                        <Chip
                          color="success"
                          icon={<CheckCircleOutlineIcon />}
                          label="Uploaded"
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {upload.status === 'error' && (
                        <Chip
                          color="error"
                          label="Error"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
          </Box>
        </Fade>
      )}

      {/* Error Alert for Max Files */}
      {fileUploads.length >= maxFiles && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Maximum number of files ({maxFiles}) reached. Remove some files to upload more.
        </Alert>
      )}
    </Box>
  );
};

export default FileUploader;
